/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MouseEventHandler,
  ReactElement,
  useEffect,
  useRef
} from 'react';
import {areEqual} from 'react-window';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalSearchResult} from '@ion/core/src/services/GlobalSearch';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import {ClickAwayListener} from '@ion/dls/src/components/listeners/ClickAwayListener';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {MapBookmarkIcon} from '@ion/dls/src/icons/MapBookmarkIcon';
import SearchView, {SavedPlacesProps} from '@ion/dls/src/maps/search/SearchView';
import IonPortal from '@ion/dls/src/render/IonPortal';
import {IconSize} from '@ion/dls/src/types/types';
import {addComponent, removeComponent} from '../helpers/simpleMapUtils';
import {useShowTool} from '../hooks/useShowTool';
import {MapToolPositions, MapTools} from '../types/types';

// defines the style prop definitions...
export interface SearchMapToolProps{
  isSelected?: boolean;
  onClick: MouseEventHandler;
  size: IconSize;
};

// defines the prop definition for SearchMapToolFactory component...
export interface SearchMapToolFactoryProps {
  view: __esri.MapView;
  topOffset?: number;
  rightOffset?: number;
  position: MapToolPositions;
  userPreferenceName?: string;
  savedSearchHistoryItems?: SavedPlacesProps[];
  activeTool: MapTools;
  setActiveTool: Function;
  searchResult?: GlobalSearchResult;
  onCloseSearchPopup?: Function;
  setSelectedTrackingSearch?: Function;
  useHereApi?: boolean;
  MapControlsBreadCrumbs?: null | ReactElement;
};

/**
 * The SearchMapTool provides the UI for search map view con that can be placed on the
 * map layers.
 *
 * ```typescript
 *
 *   <BookmarksTool size={} onClick={function() {}} />
 *
 * ```
 */
export const BookmarksTool = ({size, onClick, isSelected = false}: SearchMapToolProps) => {
  return (
    <FloatingIconButtonGroup>
      <FloatingIconButtonGroup.FloatingIconButton onClick={onClick}
        isSelected={isSelected}>
        <MapBookmarkIcon size={size} />
      </FloatingIconButtonGroup.FloatingIconButton>
    </FloatingIconButtonGroup>
  );
};

/**
 * The SearchMapToolFactory component is responsible for creating a Search map Tool/widget
 * component that provide an option to search & bookmar any location This is a stand-alone
 * tool, can be usable in any of position on the map.
 *
 * ```typescript
 *
 *  import SearchMapToolFactory from '../tools/SearchMapTool';
 *
 *  <SearchMapToolFactory view={view} />
 *
 * ```
 */
export const SearchMapToolFactory = ({
  view,
  position,
  topOffset,
  rightOffset,
  userPreferenceName,
  activeTool,
  setActiveTool,
  searchResult,
  onCloseSearchPopup,
  setSelectedTrackingSearch,
  useHereApi = false,
  savedSearchHistoryItems = [],
  MapControlsBreadCrumbs = null
}: SearchMapToolFactoryProps) => {
  const childRef                               = useRef(null);
  const isPhone                                = useIsPhone();
  const [show, toggleShow, handleClickOutside] = useShowTool({
    ref    : childRef,
    toolId : MapTools.searchMap,
    activeTool,
    setActiveTool
  });

  useEffect(() => {
    addComponent({
      id        : MapTools.mapSearchView,
      component : (
        <ClickAwayListener onClickAway={handleClickOutside} mountListener>
          <div ref={childRef}>
            <IonPortal id='map-search-view' top={topOffset} show={show}>
              <SearchView mapView={view}
                savedSearchHistoryItems={savedSearchHistoryItems}
                userPreferenceName={userPreferenceName}
                searchResult={searchResult}
                useHereApi={useHereApi}
                onCloseSearchPopup={onCloseSearchPopup}
                setSelectedTrackingSearch={setSelectedTrackingSearch}
                isVisible={show}
                onClose={toggleShow}
                MapControlsBreadCrumbs={MapControlsBreadCrumbs}
                rightOffset={rightOffset} />
            </IonPortal>
          </div>
        </ClickAwayListener>
      ),
      position,
      view
    });
  }, [show, searchResult, topOffset, rightOffset, onCloseSearchPopup, toggleShow]);

  return (
    <Tooltip title={translate('maps:controls.searchMap')} renderOnlyChildren={isPhone}>
      <div>
        <BookmarksTool size={IconSize.medium}
          onClick={toggleShow}
          isSelected={show} />
      </div>
    </Tooltip>
  );
};

export default memo(SearchMapToolFactory, areEqual);
